import { useEffect, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../../hooks/redux';
import { useAuthorization } from '../../../../../hooks/useAuthorization';
import { FilterDropdown } from '../../../../ui/FilterDropdown/FilterDropdown';
import {
  getMonitoringIntervals,
  getAISystemOverview,
  updateMonitoringInterval
} from '../../../../../services/AISystemsService';
import { fetchCurrentOverviewSuccess } from '../../../../../store/slices/aiSystemsSlice';
import { showToast } from '../../../../../store/slices/toastSlice';
import { MonitoringInterval } from '../../../../../types/AISystems.types';

const INTERVAL_LABELS: Record<MonitoringInterval, string> = {
  NONE: 'Off',
  DAILY: 'Daily',
  WEEKLY: 'Weekly',
  MONTHLY: 'Monthly'
};

export function MonitoringIntervalSelector({ aiSystemId }: { aiSystemId: string }) {
  const dispatch = useAppDispatch();
  const { can } = useAuthorization();
  const canManage = can('aiSystems.repository.manage');

  const currentOverviewAiSystemId = useAppSelector(
    (state) => state.aiSystems.currentOverviewAiSystemId
  );
  const currentOverviewInterval = useAppSelector(
    (state) => state.aiSystems.currentOverview?.repositories.monitoringInterval
  );
  // currentOverview is a single global slot, not keyed per route — fall back
  // to NONE rather than showing a stale PREVIOUS AI system's interval while
  // this system's own overview hasn't loaded yet.
  const currentInterval =
    currentOverviewAiSystemId === aiSystemId ? currentOverviewInterval ?? 'NONE' : 'NONE';

  const [options, setOptions] = useState<MonitoringInterval[]>(['NONE', 'DAILY', 'WEEKLY', 'MONTHLY']);
  const [saving, setSaving] = useState(false);

  // Tracks the AI system this component is CURRENTLY showing, so an
  // in-flight save's response can be dropped if the user has since navigated
  // to a different AI system (this component doesn't unmount across that
  // navigation — only its aiSystemId prop changes).
  const aiSystemIdRef = useRef(aiSystemId);
  useEffect(() => {
    aiSystemIdRef.current = aiSystemId;
  }, [aiSystemId]);

  useEffect(() => {
    getMonitoringIntervals()
      .then(setOptions)
      .catch(() => {
        // Falls back to the initial hardcoded list — this is a fixed, rarely
        // (if ever) changing set, so a failed fetch here isn't user-blocking.
      });
  }, []);

  const handleChange = async (value: MonitoringInterval) => {
    const requestAiSystemId = aiSystemId;
    setSaving(true);
    try {
      await updateMonitoringInterval(requestAiSystemId, value);
      const overview = await getAISystemOverview(requestAiSystemId);
      if (aiSystemIdRef.current !== requestAiSystemId) return;
      dispatch(fetchCurrentOverviewSuccess(overview));
      dispatch(showToast({ message: 'Scheduled monitoring frequency updated.', type: 'success' }));
    } catch (error) {
      console.error('Failed to update monitoring interval:', error);
    } finally {
      // Unconditional: `saving` is just a local in-flight indicator for THIS
      // request, unrelated to which AI system's data it's safe to write to
      // Redux — gating it on the ref-equality check like the dispatch above
      // would leave the control permanently disabled after switching AI
      // systems mid-save, with no way for the user to recover short of a
      // full page reload.
      setSaving(false);
    }
  };

  if (!canManage) {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-500">
        <span>Scheduled monitoring frequency:</span>
        <span className="font-medium text-gray-700">{INTERVAL_LABELS[currentInterval]}</span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <span className="text-sm text-gray-500">Scheduled monitoring frequency:</span>
      <FilterDropdown
        placeholder={INTERVAL_LABELS[currentInterval]}
        options={options.map((option) => ({ value: option, label: INTERVAL_LABELS[option] }))}
        selectedValues={[currentInterval]}
        onSelectionChange={(values) => {
          if (saving) return;
          // FilterDropdown is a multi-select toggle (clicking the already-
          // selected option removes it, leaving an empty array) — this control
          // only ever has exactly one value, so pick whichever NEW value just
          // got added and ignore a toggle-off of the current one.
          const newValue = values.find((value) => value !== currentInterval) as
            | MonitoringInterval
            | undefined;
          if (newValue) {
            handleChange(newValue);
          }
        }}
        className={saving ? 'w-auto opacity-50 pointer-events-none' : 'w-auto'}
        showSelectedCount={false}
      />
    </div>
  );
}
